import { Component } from "@theme/component";
import { DialogComponent } from "@theme/dialog";

/**
 * Size Chart Link Component
 * Triggers the size chart modal and passes the page URL
 *
 * @extends Component
 */
class SizeChartLink extends Component {
  /**
   * Gets the page URL from the data attribute
   * @returns {string} The page URL
   */
  get pageUrl() {
    return this.dataset.pageUrl || "";
  }

  /**
   * Handles click on the size chart link
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const dialog = document.getElementById("size-chart-dialog");
    if (!(dialog instanceof SizeChartDialog)) return;

    await dialog.loadAndShow(this.pageUrl);
  };
}

if (!customElements.get("size-chart-link")) {
  customElements.define("size-chart-link", SizeChartLink);
}

/**
 * Size Chart Dialog Component
 * Extends DialogComponent to handle content fetching and caching
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLElement} content - The content container
 * @property {HTMLElement} loading - The loading indicator
 * @property {HTMLElement} title - The modal title
 *
 * @extends DialogComponent
 */
class SizeChartDialog extends DialogComponent {
  requiredRefs = ["dialog", "content", "title"];

  /** @type {Map<string, string>} */
  #cachedContent = new Map();

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {string} */
  #currentUnit = "inches";

  /** @type {HTMLElement | null} */
  #loadingEl = null;

  #getDefaultUnit() {
    try {
      const locale = new Intl.Locale(navigator.language || "");
      const region = locale.region || locale.maximize().region;
      return region === "US" ? "inches" : "cm";
    } catch {
      return "inches";
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#loadingEl = this.refs.loading;
  }

  /**
   * Loads content from a page URL and displays the modal
   * @param {string} pageUrl - The URL of the size chart page
   */
  async loadAndShow(pageUrl) {
    if (!pageUrl) return;

    const { content } = this.refs;
    const loading = this.#loadingEl;

    // Show modal with loading state
    loading.hidden = false;
    content.innerHTML = "";
    content.appendChild(loading);
    this.showDialog();

    // Check cache first
    let pageContent = this.#cachedContent.get(pageUrl);

    if (!pageContent) {
      // Fetch the page content
      try {
        pageContent = await this.#fetchPageContent(pageUrl);
        if (pageContent) {
          this.#cachedContent.set(pageUrl, pageContent);
        }
      } catch (error) {
        if (error.name === "AbortError") return;
        this.dispatchEvent(
          new CustomEvent("pdp:size-chart-result", {
            bubbles: true,
            detail: { result: "load_failure" },
          }),
        );
        console.error("Failed to load size chart:", error);
        pageContent = "<p>Failed to load size chart. Please try again.</p>";
      }
    }

    // Update content
    loading.hidden = true;
    if (pageContent) {
      content.innerHTML = pageContent;
      // Initialize unit display after content is loaded
      this.#initializeUnitDisplay();
    }
  }

  /**
   * Initializes the unit display for size chart images
   */
  #initializeUnitDisplay() {
    if (!this.refs.unitToggle) return;

    let images = this.refs.content.querySelectorAll("img[data-unit]");

    // Auto-tag by position if page has a pair of plain images (inches first, cm second)
    if (images.length === 0) {
      const allImages = [...this.refs.content.querySelectorAll("img")];
      if (allImages.length >= 2) {
        allImages[0].dataset.unit = "inches";
        allImages[1].dataset.unit = "cm";
        images = this.refs.content.querySelectorAll("img[data-unit]");
      }
    }

    if (images.length === 0) {
      this.refs.unitToggle.style.display = "none";
      return;
    }

    this.refs.unitToggle.style.display = "";
    this.#currentUnit = this.#getDefaultUnit();
    this.#updateUnitDisplay();
  }

  /**
   * Updates the display to show images for the current unit
   */
  #updateUnitDisplay() {
    const images = this.refs.content.querySelectorAll("img[data-unit]");

    images.forEach((img) => {
      if (img.dataset.unit === this.#currentUnit) {
        img.classList.add("active");
      } else {
        img.classList.remove("active");
      }
    });

    // Update toggle switch active state
    if (this.refs.unitToggle) {
      this.refs.unitToggle.setAttribute("data-active", this.#currentUnit);
      this.refs.unitToggle.setAttribute(
        "aria-label",
        this.#currentUnit === "inches"
          ? "Size chart units: Inches (IN). Switch to centimeters (CM)."
          : "Size chart units: Centimeters (CM). Switch to inches (IN).",
      );
    }
  }

  /**
   * Toggles between inches and centimeters
   * @param {Event} event - The click event
   */
  toggleUnit = (event) => {
    event.preventDefault();

    // Toggle the unit
    this.#currentUnit = this.#currentUnit === "inches" ? "cm" : "inches";

    // Update the display
    this.#updateUnitDisplay();
  };

  /**
   * Fetches page content from the given URL
   * @param {string} url - The page URL to fetch
   * @returns {Promise<string|null>} The page content HTML or null
   */
  async #fetchPageContent(url) {
    // Abort any pending request
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const doc = new DOMParser().parseFromString(responseText, "text/html");

      // Extract the main content - look for common page content selectors
      const pageContent =
        doc.querySelector(".section-content-wrapper") ||
        doc.querySelector(".rte") ||
        doc.querySelector("main .shopify-section") ||
        doc.querySelector("main");

      if (pageContent) {
        // Update title from page and remove H1 from content to avoid duplication
        const pageTitle = pageContent.querySelector("h1");
        if (pageTitle) {
          if (this.refs.title) {
            this.refs.title.textContent = pageTitle.textContent || "Size Chart";
          }
          pageTitle.remove();
        }

        return pageContent.innerHTML;
      }

      return null;
    } finally {
      this.#abortController = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }
}

if (!customElements.get("size-chart-dialog")) {
  customElements.define("size-chart-dialog", SizeChartDialog);
}

export { SizeChartLink, SizeChartDialog };
